import { createBrowserRouter } from "react-router-dom";
import {
  Responsive,
  SongtradrHorizontal,
} from "@songtradr-one/components-frontend";
import {
  AppHeader,
  AppHeaderMobile,
  PortalShell,
} from "@songtradr-one/navigation-frontend";
import { AppId, routes } from "@songtradr-one/shared-common";
import {
  AppLayout,
  ErrorElement,
  INVITE_LAYOUT_ID,
  InviteLayout,
  PUBLIC_LAYOUT_ID,
  PublicLayout,
  SignOutForwarderRoute,
  USER_LAYOUT_ID,
  UserLayout,
  errorText,
} from "@songtradr-one/shared-frontend";
import { config } from "../config/config";
import { env } from "../env/env";

export const router = createBrowserRouter(
  [
    {
      ...AppLayout({
        appId: AppId.SongtradrOne,
        appName: "MassiveMusic Studio for Rights Holders",
        appNavLogo: <SongtradrHorizontal height="2rem" />,
        production: env.NODE_ENV === "production",
        coreBackendUrl:
          env.NX_PUBLIC_REMOTE_BASE_HOST &&
          env.NODE_ENV !== "production" &&
          window.location.hostname.includes(env.NX_PUBLIC_REMOTE_BASE_HOST)
            ? `${window.location.protocol}//api-${window.location.hostname}`
            : config.coreBackendUrl,
        ssoFrontendUrl: config.ssoFrontendUrl,
        assetsUrl: config.assetsUrl,
        dromoLicenseKey: config.dromoLicenseKey,
        dromoSchemaId: config.dromoSchemaId,
        analytics: { heap: { enabled: false, id: config.heapId } },
      }),
      errorElement: <ErrorElement errorText={errorText} />,
      children: [
        InviteLayout,
        UserLayout({
          header: (
            <Responsive mobile={<AppHeaderMobile />} desktop={<AppHeader />} />
          ),
        }),
        PublicLayout,
      ],
    },
  ],
  {
    async patchRoutesOnNavigation({ path, patch }) {
      if (path.startsWith(routes.portal.base)) {
        patch(USER_LAYOUT_ID, PortalShell);
      }

      if (path.startsWith(routes.statements.base)) {
        const module = await import(
          "@songtradr-one/statements-reports-frontend"
        );
        patch(USER_LAYOUT_ID, module.StatementsShell);
      }

      if (path.startsWith(routes.statementImports.base)) {
        const module = await import(
          "@songtradr-one/statements-reports-frontend"
        );
        patch(USER_LAYOUT_ID, module.StatementImportsShell);
      }

      if (path.startsWith(routes.resources.base)) {
        const module = await import("@songtradr-one/resources-frontend");
        patch(USER_LAYOUT_ID, module.ResourcesShell);
      }

      if (path.startsWith(routes.payouts.base)) {
        const module = await import("@songtradr-one/payouts-frontend");
        patch(USER_LAYOUT_ID, module.PayoutsShell);
      }

      if (path.startsWith(routes.payoutAdministration.base)) {
        const module = await import("@songtradr-one/payouts-frontend");
        patch(USER_LAYOUT_ID, module.PayoutAdministrationShell);
      }

      if (path.startsWith(routes.socialClearances.base)) {
        const module = await import("@songtradr-one/social-clearance-frontend");
        patch(USER_LAYOUT_ID, module.SocialClearancesShell);
      }

      if (path.startsWith(routes.labs.base)) {
        const module = await import("@songtradr-one/labs-frontend");
        patch(USER_LAYOUT_ID, module.LabsShell);
      }

      if (
        path.startsWith(routes.orgs.base) &&
        !path.startsWith(routes.orgs.inviteAccept.replace(":id", ""))
      ) {
        const module = await import("@songtradr-one/orgs-frontend");
        patch(USER_LAYOUT_ID, module.OrgsShell);
      }

      if (path.startsWith(routes.orgs.inviteAccept.replace(":id", ""))) {
        const module = await import("@songtradr-one/orgs-frontend");
        patch(INVITE_LAYOUT_ID, module.OrgsInviteShell);
      }

      if (path.startsWith(routes.signing.base)) {
        const module = await import("@songtradr-one/signing-service-frontend");
        patch(PUBLIC_LAYOUT_ID, module.SigningServiceShell);
      }

      if (path.startsWith(routes.msa.base)) {
        const module = await import(
          "@songtradr-one/master-service-agreement-frontend"
        );
        patch(PUBLIC_LAYOUT_ID, module.MsaShell);
      }

      if (path.startsWith(routes.musicIngestion.base)) {
        const module = await import("@songtradr-one/music-ingestion-frontend");
        patch(USER_LAYOUT_ID, module.MusicIngestionShell);
      }

      if (path.startsWith(routes.legal.base)) {
        const module = await import("@songtradr-one/legal-frontend");
        patch(USER_LAYOUT_ID, module.LegalShell);
      }

      if (path.startsWith(routes.reporting.base)) {
        const module = await import("@songtradr-one/reporting-frontend");
        patch(USER_LAYOUT_ID, module.ReportingShell);
      }

      if (path.startsWith(routes.distribution.base)) {
        const module = await import("@songtradr-one/distribution-frontend");
        patch(USER_LAYOUT_ID, module.DistributionShell);
      }

      if (path === routes.signout.base) {
        patch(PUBLIC_LAYOUT_ID, SignOutForwarderRoute);
      }
    },
  },
);
